import { useState, useRef, useEffect } from 'react';
import FloatingPortal from './FloatingPortal';

interface DatePickerProps {
    label?: string;
    value?: Date | string;
    onChange: (date: Date | undefined) => void;
    placeholder?: string;
    required?: boolean;
    error?: string;
    minDate?: Date;
    maxDate?: Date;
}

/**
 * Modern Date Picker Component
 * A clean, Tailwind-styled date picker that's more visually appealing than native input[type="date"]
 */
export default function DatePicker({
    label,
    value,
    onChange,
    placeholder = 'Select a date',
    required = false,
    error,
    minDate,
    maxDate,
}: DatePickerProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(
        value ? (typeof value === 'string' ? new Date(value) : value) : undefined
    );
    const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());

    const anchorRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (value) {
            const date = typeof value === 'string' ? new Date(value) : value;
            setSelectedDate(date);
            setCurrentMonth(date);
        }
    }, [value]);

    // Close picker when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (anchorRef.current?.contains(target) || panelRef.current?.contains(target)) {
                return;
            }
            setIsOpen(false);
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const formatDate = (date: Date | undefined): string => {
        if (!date) return '';
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const getDaysInMonth = (date: Date): Date[] => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();

        const days: Date[] = [];

        // Add empty slots for days before the month starts
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(new Date(0)); // Placeholder for empty days
        }

        // Add all days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(new Date(year, month, day));
        }

        return days;
    };

    const handleDateSelect = (date: Date) => {
        if (date.getTime() === 0) return; // Skip placeholder days

        // Check if date is within range
        if (minDate && date < minDate) return;
        if (maxDate && date > maxDate) return;

        setSelectedDate(date);
        onChange(date);
        setIsOpen(false);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedDate(undefined);
        onChange(undefined);
    };

    const goToPreviousMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    const goToNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    const monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    const isDateDisabled = (date: Date): boolean => {
        if (date.getTime() === 0) return true;
        if (minDate && date < minDate) return true;
        if (maxDate && date > maxDate) return true;
        return false;
    };

    const isDateSelected = (date: Date): boolean => {
        if (!selectedDate || date.getTime() === 0) return false;
        return (
            date.getDate() === selectedDate.getDate() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getFullYear() === selectedDate.getFullYear()
        );
    };

    const isToday = (date: Date): boolean => {
        if (date.getTime() === 0) return false;
        const today = new Date();
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    };

    const days = getDaysInMonth(currentMonth);

    return (
        <div className="relative w-full">
            {label && (
                <label className="block text-sm font-medium text-[#1e293b] mb-2">
                    {label}
                    {required && <span className="text-[#ef4444] ml-1">*</span>}
                </label>
            )}

            {/* Input Display */}
            <div ref={anchorRef} className="relative">
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-full px-4 py-2.5 bg-white border-2 rounded-lg text-left transition-all focus:outline-none flex items-center justify-between ${
                        error
                            ? 'border-[#ef4444] focus:border-[#ef4444] focus:ring-4 focus:ring-[#ef4444]/10'
                            : isOpen
                              ? 'border-[#6e64f0] ring-4 ring-[#6e64f0]/10'
                              : 'border-[#e2e8f0] focus:border-[#6e64f0] focus:ring-4 focus:ring-[#6e64f0]/10'
                    }`}
                >
                    <span className={selectedDate ? 'text-[#1e293b]' : 'text-[#94a3b8]'}>
                        {selectedDate ? formatDate(selectedDate) : placeholder}
                    </span>
                    <div className="flex items-center gap-2">
                        <svg
                            className="w-5 h-5 text-[#64748b]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                    </div>
                </button>

                {/* Clear Button - Positioned absolutely to avoid nesting inside button */}
                {selectedDate && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-10 top-1/2 -translate-y-1/2 p-1 text-[#64748b] hover:text-[#ef4444] transition-colors z-10"
                    >
                        <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                )}
            </div>

            {error && <p className="mt-1 text-xs text-[#ef4444]">{error}</p>}

            <FloatingPortal
                anchorRef={anchorRef}
                panelRef={panelRef}
                isOpen={isOpen}
                width={320}
                maxHeight={400}
                className="bg-white shadow-2xl border border-[#e2e8f0] rounded-lg p-4 overflow-y-auto"
            >
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-4">
                        <button
                            type="button"
                            onClick={goToPreviousMonth}
                            className="p-2 hover:bg-[#f8fafc] rounded-lg transition-colors"
                        >
                            <svg
                                className="w-5 h-5 text-[#64748b]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                        <h3 className="text-sm font-semibold text-[#1e293b]">
                            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                        </h3>
                        <button
                            type="button"
                            onClick={goToNextMonth}
                            className="p-2 hover:bg-[#f8fafc] rounded-lg transition-colors"
                        >
                            <svg
                                className="w-5 h-5 text-[#64748b]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Day Names */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map((day) => (
                            <div
                                key={day}
                                className="text-center text-xs font-medium text-[#64748b] py-2"
                            >
                                {day}
                            </div>
                        ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1">
                        {days.map((date, index) => {
                            const disabled = isDateDisabled(date);
                            const selected = isDateSelected(date);
                            const today = isToday(date);
                            const isEmpty = date.getTime() === 0;

                            return (
                                <button
                                    key={index}
                                    type="button"
                                    onClick={() => handleDateSelect(date)}
                                    disabled={disabled || isEmpty}
                                    className={`
                                        aspect-square p-2 text-sm rounded-lg transition-all
                                        ${isEmpty ? 'invisible' : ''}
                                        ${disabled && !isEmpty ? 'text-[#cbd5e1] cursor-not-allowed' : ''}
                                        ${selected ? 'bg-[#6e64f0] text-white font-semibold' : ''}
                                        ${!selected && !disabled && !isEmpty ? 'hover:bg-[#f5f3ff] text-[#1e293b]' : ''}
                                        ${today && !selected ? 'ring-2 ring-[#6e64f0] ring-inset' : ''}
                                    `}
                                >
                                    {!isEmpty && date.getDate()}
                                </button>
                            );
                        })}
                    </div>
            </FloatingPortal>
        </div>
    );
}
